import React, { useEffect, useContext, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, Paper, Alert, LinearProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAppStateContext } from '../../state/AppContextProvider';
import { useSuccess } from '../../state/SuccessProvider';
import { HttpServiceContext } from '../../state/HttpProvider';
import { refreshAccessToken } from '../../auth/oauthService';
import { useConfig } from '../../config/ConfigProvider';
import { useOAuth } from '../../auth/OAuthProvider';

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshApprovalStatus, isApproved, loadingApprovalStatus } = useAppStateContext();
  const { triggerSuccess } = useSuccess();
  const { httpService } = useContext(HttpServiceContext);
  const config = useConfig();
  const { refreshUser } = useOAuth();
  const [countdown, setCountdown] = useState(10);
  const [hasTriggeredConfetti, setHasTriggeredConfetti] = useState(false);
  const [checkingApproval, setCheckingApproval] = useState(true);
  const [tokensRefreshed, setTokensRefreshed] = useState(false);

  useEffect(() => {
    console.log('🔍 SubscriptionSuccess mounted - starting approval monitoring');
    
    // Trigger confetti celebration immediately
    if (!hasTriggeredConfetti) {
      triggerSuccess('subscription', {
        message: "Payment Successful! 🎉",
        subMessage: "Your subscription is being processed by our secure payment system...",
        autoRedirectDelay: 0 // We'll handle redirect manually
      });
      setHasTriggeredConfetti(true);
    }
    
    let pollInterval: NodeJS.Timeout | null = null;
    let timeout: NodeJS.Timeout | null = null;
    
    // Refresh tokens first to get updated user data with new roles
    const refreshAndPoll = async () => {
      if (!tokensRefreshed) {
        try {
          console.log('🔄 Refreshing JWT tokens to fetch updated roles from Cosmos DB...');
          const apiUrl = config.apiBaseUrl || '';
          const refreshed = await refreshAccessToken(apiUrl);
          if (refreshed) {
            console.log('✅ JWT tokens refreshed successfully');
            // Now refresh the user state to get the new token data (silent = true to avoid unmounting)
            await refreshUser(true);
            console.log('✅ User state updated with new roles');
            setTokensRefreshed(true);
          } else {
            console.log('ℹ️ Token refresh returned false (may not have valid refresh token yet), continuing with polling...');
            setTokensRefreshed(true); // Proceed anyway
          }
        } catch (error) {
          // Expected to fail if Stripe webhook hasn't processed yet - this is normal
          console.log('ℹ️ Token refresh not ready yet (Stripe webhook may still be processing), will retry via polling...');
          setTokensRefreshed(true); // Proceed anyway
        }
      }
    };
    
    // Start polling for approval status
    const pollApprovalStatus = async () => {
      console.log('🔄 Polling approval status...');
      await refreshApprovalStatus();
      
      // After each poll, try to refresh user to pick up role changes (silent = true)
      try {
        await refreshUser(true);
        console.log('🔄 User state refreshed after polling');
      } catch (err) {
        console.log('⚠️ Failed to refresh user after polling, will retry on next poll');
      }
    };
    
    // Refresh tokens first, then start polling
    refreshAndPoll().then(() => {
      pollApprovalStatus();
      pollInterval = setInterval(pollApprovalStatus, 2000);
      
      // Stop polling after 30 seconds to prevent infinite polling
      timeout = setTimeout(() => {
        console.log('⏰ Stopping approval polling after 30 seconds');
        if (pollInterval) clearInterval(pollInterval);
        setCheckingApproval(false);
      }, 30000);
    });
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
      if (timeout) clearTimeout(timeout);
    };
  }, [refreshApprovalStatus, triggerSuccess, hasTriggeredConfetti, tokensRefreshed, refreshUser, config.apiBaseUrl]);

  // Stop polling when user becomes approved
  useEffect(() => {
    if (isApproved === true) {
      console.log('✅ User approved! Stopping polling.');
      setCheckingApproval(false);
    }
  }, [isApproved]);

  // Auto-redirect when approved or after countdown
  useEffect(() => {
    if (isApproved) {
      // If approved, redirect immediately with success message
      const timer = setTimeout(() => {
        triggerSuccess('approved', {
          message: "Access Confirmed! ✨",
          subMessage: "Your account is now fully activated. Welcome aboard!",
          autoRedirectDelay: 2000,
          onComplete: () => navigate('/')
        });
      }, 2000); // 2 second delay to show subscription success first
      return () => clearTimeout(timer);
    } else if (!loadingApprovalStatus && countdown > 0) {
      // If not approved yet, start countdown
      const timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      // Countdown reached 0, redirect to home
      navigate('/');
    }
    return undefined;
  }, [isApproved, loadingApprovalStatus, countdown, navigate, triggerSuccess]);

  const handleContinue = () => {
    navigate('/');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          textAlign: 'center',
        }}
      >
        <Box sx={{ mb: 3 }}>
          <CheckCircleIcon
            sx={{
              fontSize: 80,
              color: 'success.main',
              mb: 2,
            }}
          />
        </Box>

        <Typography variant="h4" gutterBottom color="success.main">
          Subscription Successful!
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Thank you for subscribing! Your payment has been processed successfully.
          You now have access to all features.
        </Typography>

        {isApproved ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            Your access has been confirmed! Redirecting you to the application...
          </Alert>
        ) : checkingApproval ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            Processing your subscription... Please wait.
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              Your payment is being processed. You should have access shortly.
              {!loadingApprovalStatus && (
                <> Redirecting to home page in {countdown} seconds...</>
              )}
            </Alert>
            {!loadingApprovalStatus && (
              <LinearProgress 
                variant="determinate" 
                value={((10 - countdown) / 10) * 100} 
                sx={{ mb: 3 }}
              />
            )}
          </>
        )}

        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleContinue}
          sx={{ minWidth: 200 }}
        >
          Continue to App
        </Button>
      </Paper>
    </Box>
  );
};

export default SubscriptionSuccess;